import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Event, EventAttendee } from '../types';

// ── Query keys ─────────────────────────────────────────────────────────────────

export const eventKeys = {
  all:       ['events'] as const,
  lists:     () => [...eventKeys.all, 'list'] as const,
  byMonth:   (year: number, month: number) =>
               [...eventKeys.lists(), year, month] as const,
  upcoming:  () => [...eventKeys.all, 'upcoming'] as const,
  detail:    (id: string) => [...eventKeys.all, 'detail', id] as const,
  attendees: (id: string) => [...eventKeys.all, 'attendees', id] as const,
  count:     () => [...eventKeys.all, 'count'] as const,
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function monthRange(year: number, month: number) {
  // Returns ISO strings for the first and last day of the calendar grid
  // We extend ±1 week to cover leading/trailing days visible in a grid
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0, 23, 59, 59);
  // Pull back to Sunday before the 1st
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay());
  // Push forward to Saturday after the last
  const gridEnd = new Date(last);
  gridEnd.setDate(last.getDate() + (6 - last.getDay()));
  gridEnd.setHours(23, 59, 59, 999);
  return { from: gridStart.toISOString(), to: gridEnd.toISOString() };
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

/** All events for a given calendar month (including grid overflow days). */
export function useEventsByMonth(year: number, month: number) {
  return useQuery({
    queryKey: eventKeys.byMonth(year, month),
    queryFn: async () => {
      const { from, to } = monthRange(year, month);
      const { data, error } = await (supabase as any)
        .from('events')
        .select('*')
        .gte('starts_at', from)
        .lte('starts_at', to)
        .neq('status', 'cancelled')
        .order('starts_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Event[];
    },
    staleTime: 60_000,
  });
}

/** Upcoming events (today + future, not cancelled), used in dashboard KPI. */
export function useUpcomingEvents(limit = 5) {
  return useQuery({
    queryKey: eventKeys.upcoming(),
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data, error } = await (supabase as any)
        .from('events')
        .select('*')
        .gte('starts_at', now)
        .neq('status', 'cancelled')
        .order('starts_at', { ascending: true })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as Event[];
    },
    staleTime: 60_000,
  });
}

/** Count of upcoming events (not cancelled, starts_at >= now). */
export function useUpcomingEventCount() {
  return useQuery({
    queryKey: eventKeys.count(),
    queryFn: async () => {
      const now = new Date().toISOString();
      const { count, error } = await (supabase as any)
        .from('events')
        .select('id', { count: 'exact', head: true })
        .gte('starts_at', now)
        .neq('status', 'cancelled');
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 60_000,
  });
}

/** Single event detail. */
export function useEvent(id: string | undefined) {
  return useQuery({
    queryKey: eventKeys.detail(id!),
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('events')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as Event;
    },
    enabled: !!id,
  });
}

/** Attendees for a single event, joined with basic contact data. */
export function useEventAttendees(eventId: string | undefined) {
  return useQuery({
    queryKey: eventKeys.attendees(eventId!),
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('event_attendees')
        .select(`
          *,
          contact:contacts(id, full_name, phone, category)
        `)
        .eq('event_id', eventId)
        .order('invited_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as EventAttendee[];
    },
    enabled: !!eventId,
  });
}
